import { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Clock, Target, Calendar as CalendarIcon, Brain, Trash2, ChevronDown, ChevronUp, Repeat } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { CalendarEvent, WeekViewData } from '@/types/calendar';
import { calendarService } from '@/services/calendarService';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';
import { universalCompletionService } from '@/services/universalCompletionService';
import { devDateTimeService } from '@/services/devDateTimeService';

/**
 * Determines if a calendar event should be interactable (clickable, show progress).
 *
 * Rules:
 * - One-time events: Always interactable
 * - Recurring events today or in the past: Always interactable
 * - Recurring events in the future: Only the immediate next occurrence (within 1 day) is interactable
 *
 * This prevents users from accidentally completing tasks on far-future recurring instances,
 * which would affect the completion state of the current recurring cycle.
 */
function isEventInteractable(event: CalendarEvent): boolean {
  if (!event.isRecurring) return true;

  const now = devDateTimeService.now();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const eventDate = new Date(
    event.startTime.getFullYear(),
    event.startTime.getMonth(),
    event.startTime.getDate()
  );

  // Today or past: interactable
  if (eventDate <= today) return true;

  // Future: only if within 1 day (the next immediate occurrence)
  const daysUntilEvent = Math.floor(
    (eventDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );

  return daysUntilEvent <= 1;
}

interface WeeklyCalendarViewProps {
  onCreateSchedule?: (listId?: string, date?: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onOptimizeWeek?: () => void;
}

export function WeeklyCalendarView({ onCreateSchedule, onEventClick, onOptimizeWeek }: WeeklyCalendarViewProps) {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    // Start from today for rolling 7-day view
    const today = devDateTimeService.now();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // Local state to trigger re-render when schedules are modified directly
  const [scheduleVersion, setScheduleVersion] = useState(0);

  const { lists, completionVersion } = useListsStore();
  const { goals } = useGoalsStore();
  const { aspirations } = useAspirationsStore();

  const weekViewData = useMemo(() => {
    console.log('📅 WeeklyCalendarView: Regenerating weekViewData with completionVersion:', completionVersion, 'scheduleVersion:', scheduleVersion);
    return calendarService.getWeekViewData(currentWeekStart, lists, goals, aspirations);
  }, [currentWeekStart, lists, goals, aspirations, completionVersion, scheduleVersion]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newWeekStart);
  };

  const goToToday = () => {
    // Reset to today for rolling 7-day view
    const today = devDateTimeService.now();
    today.setHours(0, 0, 0, 0);
    setCurrentWeekStart(today);
  };

  const handleAIReschedule = async (event: CalendarEvent) => {
    try {
      const list = lists.find(l => l.id === event.listId);
      if (!list) {
        alert('Associated list not found');
        return;
      }

      const goal = list.goalId ? goals.find(g => g.id === list.goalId) : undefined;
      const profile = personaService.getProfile();
      const schedules = calendarService.getAllSchedules();

      // Default working hours - in a real app this should come from user preferences
      const DEFAULT_WORKING_HOURS = {
        monday: { start: '09:00', end: '17:00', enabled: true },
        tuesday: { start: '09:00', end: '17:00', enabled: true },
        wednesday: { start: '09:00', end: '17:00', enabled: true },
        thursday: { start: '09:00', end: '17:00', enabled: true },
        friday: { start: '09:00', end: '17:00', enabled: true },
        saturday: { start: '10:00', end: '16:00', enabled: false },
        sunday: { start: '10:00', end: '16:00', enabled: false }
      };

      // Get current completion context for items
      const contextDate = new Date();
      const listData = universalCompletionService.getListDataForContext(list, contextDate);

      const suggestion = await aiService.generateScheduleSuggestion({
        userInput: `Find a better time for "${event.title}" considering my current priorities and schedule`,
        listToSchedule: {
          id: list.id,
          title: list.title,
          description: list.description,
          priority: list.priority,
          estimatedHours: list.estimatedHours,
          deadline: list.deadline?.toISOString(),
          items: listData.items.map(item => ({
            content: item.content,
            estimatedMinutes: item.estimatedMinutes,
            completed: item.completed
          }))
        },
        linkedGoal: goal ? {
          id: goal.id,
          title: goal.title,
          smartCriteria: goal.smartCriteria
        } : undefined,
        existingSchedules: schedules
          .filter(s => s.isActive && s.id !== event.scheduleId)
          .map(s => ({
            listId: s.listId,
            listTitle: lists.find(l => l.id === s.listId)?.title || '',
            scheduledDate: s.scheduledDate?.toISOString() || '',
            scheduledTime: s.scheduledTime || '',
            duration: 60,
            isRecurring: s.type !== 'one_time'
          })),
        userContext: {
          workingHours: DEFAULT_WORKING_HOURS,
          preferredTimes: profile.preferredWorkStyles,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          currentDate: new Date().toISOString()
        }
      });

      if (suggestion.primaryTimeSlot) {
        const confirmMessage = `Move "${event.title}" to ${new Date(suggestion.primaryTimeSlot.date).toLocaleDateString()} at ${suggestion.primaryTimeSlot.time}?\n\nReason: ${suggestion.reasoning[0]}`;
        
        if (confirm(confirmMessage)) {
          calendarService.updateSchedule(event.scheduleId, {
            scheduledDate: new Date(suggestion.primaryTimeSlot.date),
            scheduledTime: suggestion.primaryTimeSlot.time
          });
          
          // Trigger re-render by updating schedule version
          setScheduleVersion(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('Failed to reschedule:', error);
      alert('Failed to find better time. Please try again.');
    }
  };

  const handleDeleteSchedule = (event: CalendarEvent) => {
    if (confirm(`Are you sure you want to delete the schedule for "${event.title}"?`)) {
      calendarService.deleteSchedule(event.scheduleId);
      setScheduleVersion(prev => prev + 1);
    }
  };

  const formatWeekRange = () => {
    const start = weekViewData.weekStart.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
    const end = weekViewData.weekEnd.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    return `${start} - ${end}`;
  };

  return (
    <Card className="w-full" data-testid="weekly-calendar-view">
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <CardTitle className="flex items-center text-lg sm:text-xl">
            <CalendarIcon className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Week of {formatWeekRange()}</span>
            <span className="sm:hidden">{formatWeekRange()}</span>
          </CardTitle>
          <div className="flex items-center space-x-1 sm:space-x-2 w-full sm:w-auto">
            <Button variant="outline" size="sm" onClick={() => navigateWeek('prev')} className="flex-1 sm:flex-none">
              <ChevronLeft className="h-4 w-4" />
              <span className="sm:hidden ml-1">Prev</span>
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="flex-1 sm:flex-none">
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigateWeek('next')} className="flex-1 sm:flex-none">
              <ChevronRight className="h-4 w-4" />
              <span className="sm:hidden ml-1">Next</span>
            </Button>
            {onOptimizeWeek && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onOptimizeWeek} 
                className="h-8 w-8"
                title="AI Schedule Optimizer"
              >
                <Brain className="h-4 w-4 text-purple-600" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Mobile view: Stack days vertically */}
        <div className="block md:hidden space-y-4">
          {weekViewData.days.map((day) => {
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayName = dayNames[day.date.getDay()];
            return (
              <div key={day.date.toISOString()} className="border rounded-lg">
                <div className="p-3 border-b bg-muted/50 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`text-lg font-semibold ${
                      day.isToday ? 'text-blue-600' : 'text-foreground'
                    }`}>
                      {day.date.getDate()}
                    </div>
                    <div className="text-sm font-medium text-muted-foreground">
                      {dayName}
                    </div>
                  </div>
                  {/* Plus button moved to header dropdown */}
                </div>
                <div className="p-3">
                  <MobileDayContent
                    day={day}
                    onEventClick={onEventClick}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Desktop view: 7-column grid */}
        <div className="hidden md:grid md:grid-cols-7 gap-2">
          {/* Day headers */}
          {weekViewData.days.map((day) => {
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const dayName = dayNames[day.date.getDay()];
            return (
              <div key={day.date.toISOString()} className="p-3 text-center text-sm font-medium text-muted-foreground border-b">
                {dayName}
              </div>
            );
          })}
          
          {/* Day columns */}
          {weekViewData.days.map((day, _index) => (
            <CalendarDayColumn
              key={day.date.toISOString()}
              day={day}
              onCreateSchedule={(date) => onCreateSchedule?.(undefined, date)}
              onEventClick={onEventClick}
              onAIReschedule={handleAIReschedule}
              onDeleteSchedule={handleDeleteSchedule}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

interface CalendarDayColumnProps {
  day: WeekViewData['days'][0];
  onCreateSchedule?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onAIReschedule?: (event: CalendarEvent) => void;
  onDeleteSchedule?: (event: CalendarEvent) => void;
}

function CalendarDayColumn({ day, onEventClick, onAIReschedule, onDeleteSchedule }: CalendarDayColumnProps) {
  return (
    <div 
      className={`min-h-[300px] p-2 border-r border-gray-100`}
    >
      {/* Date header */}
      <div className="flex items-center justify-between mb-2">
        <div className={`text-lg font-semibold ${
          day.isToday ? 'text-blue-600' : 'text-foreground'
        }`}>
          {day.date.getDate()}
        </div>
        {/* Plus button moved to header dropdown */}
      </div>

      {/* Time utilization bar */}
      {day.availableMinutes > 0 && (
        <div className="mb-2">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all ${
                day.totalScheduledMinutes > day.availableMinutes
                  ? 'bg-red-500'
                  : day.totalScheduledMinutes > day.availableMinutes * 0.8
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ 
                width: `${Math.min(100, (day.totalScheduledMinutes / day.availableMinutes) * 100)}%` 
              }}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {Math.round(day.totalScheduledMinutes / 60 * 10) / 10}h / {Math.round(day.availableMinutes / 60 * 10) / 10}h
          </div>
        </div>
      )}

      {/* Events */}
      <div className="space-y-1">
        {day.events.map((event) => (
          <CalendarEventItem
            key={event.id}
            event={event}
            onClick={() => onEventClick?.(event)}
            onAIReschedule={onAIReschedule}
            onDelete={onDeleteSchedule}
          />
        ))}
      </div>

    </div>
  );
}

interface CalendarEventItemProps {
  event: CalendarEvent;
  onClick?: () => void;
  onAIReschedule?: (event: CalendarEvent) => void;
  onDelete?: (event: CalendarEvent) => void;
}

function CalendarEventItem({ event, onClick, onAIReschedule, onDelete }: CalendarEventItemProps) {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine if this event can be interacted with
  const interactable = isEventInteractable(event);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleCloseContextMenu = () => {
    setShowContextMenu(false);
  };

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(false);
    };

    if (showContextMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }

    return () => {}; // Return empty cleanup function for all paths
  }, [showContextMenu]);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDuration = () => {
    if (!event.endTime) return '';
    const duration = Math.round((event.endTime.getTime() - event.startTime.getTime()) / (1000 * 60));
    if (duration < 60) return `${duration}m`;
    return `${Math.floor(duration / 60)}h${duration % 60 ? ` ${duration % 60}m` : ''}`;
  };

  // Check if there are expandable details to show - only if interactable
  const hasExpandableContent = interactable && (
    event.listProgress !== undefined ||
    event.goalTitle ||
    event.aspirationTitle ||
    event.description
  );

  return (
    <>
      <div
        className={`p-2 rounded-lg transition-shadow text-xs border-l-2 ${
          interactable ? 'cursor-pointer hover:shadow-sm' : 'cursor-default opacity-75'
        }`}
        style={{ borderLeftColor: event.color }}
        onClick={interactable ? onClick : undefined}
        onContextMenu={interactable ? handleContextMenu : undefined}
      >
        {/* Top row: Recurring icon (if applicable) and expand button */}
        {(event.isRecurring || hasExpandableContent) && (
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center">
              {event.isRecurring && (
                <span title="Recurring event" className="flex items-center">
                  <Repeat className="h-3 w-3 text-purple-500" />
                </span>
              )}
            </div>
            {hasExpandableContent && (
              <button
                onClick={handleExpandClick}
                className="p-0.5 hover:bg-gray-100 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 flex items-center justify-center"
                aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
              >
                {isExpanded ? (
                  <ChevronUp className="h-3 w-3 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                )}
              </button>
            )}
          </div>
        )}

        {/* Time display - stacked format for narrow columns */}
        <div className="flex items-start text-muted-foreground mb-1">
          <Clock className="h-3 w-3 mr-1 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col leading-tight">
            <span>{formatTime(event.startTime)}</span>
            {getDuration() && <span className="text-[10px]">{getDuration()}</span>}
          </div>
        </div>

        {/* Title */}
        <div className="font-medium text-foreground truncate">
          {event.title}
        </div>

        {/* Expandable Details - only shown if interactable */}
        {isExpanded && interactable && (
          <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
            {/* Progress Bar */}
            {event.listProgress !== undefined && (
              <div className="flex items-center">
                <div className="w-full bg-gray-200 rounded-full h-1 mr-2">
                  <div
                    className="bg-blue-500 h-1 rounded-full transition-all"
                    style={{ width: `${event.listProgress}%` }}
                  />
                </div>
                <span className="text-muted-foreground text-xs whitespace-nowrap">
                  {event.listProgress}%
                </span>
              </div>
            )}

            {/* Goal */}
            {event.goalTitle && (
              <div className="flex items-center">
                <Target className="h-3 w-3 text-muted-foreground mr-1 flex-shrink-0" />
                <span className="text-muted-foreground truncate">
                  {event.goalTitle}
                </span>
              </div>
            )}

            {/* Aspiration */}
            {event.aspirationTitle && (
              <div className="flex items-center">
                <span className="text-muted-foreground truncate text-xs">
                  Aspiration: {event.aspirationTitle}
                </span>
              </div>
            )}

            {/* Description */}
            {event.description && (
              <div className="text-muted-foreground text-xs line-clamp-2">
                {event.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context Menu - only shown if interactable */}
      {showContextMenu && interactable && (
        <div
          className="fixed bg-background shadow-lg rounded-md border py-1 z-50 min-w-[160px]"
          style={{
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
          }}
        >
          {aiService.isConfigured() && onAIReschedule && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAIReschedule(event);
                handleCloseContextMenu();
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 w-full text-left"
            >
              <Brain className="h-4 w-4" />
              Find Better Time
            </button>
          )}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(event);
                handleCloseContextMenu();
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-100 w-full text-left text-red-600"
            >
              <Trash2 className="h-4 w-4" />
              Delete Schedule
            </button>
          )}
        </div>
      )}
    </>
  );
}


interface MobileDayContentProps {
  day: WeekViewData['days'][0];
  onEventClick?: (event: CalendarEvent) => void;
}

function MobileDayContent({ day, onEventClick }: MobileDayContentProps) {
  return (
    <div className="space-y-3">
      {/* Time utilization bar */}
      {day.availableMinutes > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-medium text-foreground">Scheduled Time</div>
            <div className="text-sm text-muted-foreground">
              {Math.round(day.totalScheduledMinutes / 60 * 10) / 10}h / {Math.round(day.availableMinutes / 60 * 10) / 10}h
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all ${
                day.totalScheduledMinutes > day.availableMinutes
                  ? 'bg-red-500'
                  : day.totalScheduledMinutes > day.availableMinutes * 0.8
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ 
                width: `${Math.min(100, (day.totalScheduledMinutes / day.availableMinutes) * 100)}%` 
              }}
            />
          </div>
        </div>
      )}

      {/* Events */}
      {day.events.length > 0 && (
        <div>
          <div className="text-sm font-medium text-foreground mb-2">Scheduled Events</div>
          <div className="space-y-2">
            {day.events.map((event) => (
              <MobileCalendarEventItem
                key={event.id}
                event={event}
                onClick={() => onEventClick?.(event)}
              />
            ))}
          </div>
        </div>
      )}


      {/* Empty state */}
      {day.events.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <CalendarIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No events scheduled</p>
        </div>
      )}
    </div>
  );
}

interface MobileCalendarEventItemProps {
  event: CalendarEvent;
  onClick?: () => void;
}

function MobileCalendarEventItem({ event, onClick }: MobileCalendarEventItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Determine if this event can be interacted with
  const interactable = isEventInteractable(event);

  console.log('MobileCalendarEventItem: Rendering event.listProgress', event.listProgress);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getDuration = () => {
    if (!event.endTime) return '';
    const duration = Math.round((event.endTime.getTime() - event.startTime.getTime()) / (1000 * 60));
    if (duration < 60) return `${duration}m`;
    return `${Math.floor(duration / 60)}h${duration % 60 ? ` ${duration % 60}m` : ''}`;
  };

  const handleExpandClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Check if there are expandable details to show - only if interactable
  const hasExpandableContent = interactable && (
    event.listProgress !== undefined ||
    event.goalTitle ||
    event.aspirationTitle ||
    event.description
  );

  return (
    <div
      className={`p-3 rounded-lg transition-all border border-gray-200 border-l-4 ${
        interactable ? 'cursor-pointer hover:shadow-md' : 'cursor-default opacity-75'
      }`}
      style={{ borderLeftColor: event.color }}
      onClick={interactable ? onClick : undefined}
    >
      {/* Time and Duration - First Row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center text-sm text-muted-foreground">
          <Clock className="h-4 w-4 mr-1" />
          {formatTime(event.startTime)}
          {getDuration() && ` (${getDuration()})`}
          {event.isRecurring && (
            <span title="Recurring event">
              <Repeat className="h-4 w-4 ml-2 text-purple-500" />
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasExpandableContent && (
            <button
              onClick={handleExpandClick}
              className="p-1 hover:bg-gray-100 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 flex items-center justify-center"
              aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          )}
          {!interactable && event.isRecurring && (
            <span className="text-xs text-muted-foreground italic">Future</span>
          )}
        </div>
      </div>

      {/* Title - Second Row */}
      <div className="font-medium text-foreground text-sm">
        {event.title}
      </div>

      {/* Expandable Details - only shown if interactable */}
      {isExpanded && interactable && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          {/* Progress Bar */}
          {event.listProgress !== undefined && (
            <div className="flex items-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${event.listProgress}%` }}
                />
              </div>
              <span className="text-muted-foreground text-sm">
                {event.listProgress}%
              </span>
            </div>
          )}

          {/* Goal */}
          {event.goalTitle && (
            <div className="flex items-center">
              <Target className="h-4 w-4 text-muted-foreground mr-1" />
              <span className="text-muted-foreground text-sm">
                {event.goalTitle}
              </span>
            </div>
          )}

          {/* Aspiration */}
          {event.aspirationTitle && (
            <div className="flex items-center text-sm text-muted-foreground">
              Aspiration: {event.aspirationTitle}
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="text-muted-foreground text-sm line-clamp-3">
              {event.description}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

